import { useEffect, useState, useCallback, useContext } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { SEARCH_DEBOUNCE_MS, MIN_SEARCH_LENGTH } from '@/config/search';
import { PlaceNamesContext, Place } from '@/contexts/PlaceNamesContext';
import { formatPlaceName } from '@/lib/utils';
import { makeSearchKey } from '@/utils/makeSearchKey';

interface UseJurisdictionSearchResult {
  searchInputValue: string;
  setSearchInputValue: (val: string) => void;
  placeOptions: (Place & { displayName: string })[];
  isSearching: boolean;
  isDebouncing: boolean;
  searchError: string | null;
  clearResults: () => void;
}

/**
 * Handles debounced jurisdiction search using a client-side Trie.
 * Keeps async + debounce logic out of the page.
 */
export const useJurisdictionSearch = (isOpen: boolean): UseJurisdictionSearchResult => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [placeOptions, setPlaceOptions] = useState<(Place & { displayName: string })[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const context = useContext(PlaceNamesContext);
  if (!context) {
    throw new Error('useJurisdictionSearch must be used within a PlaceNamesProvider');
  }
  const { trie, placeMap } = context;

  const debouncedSearchInputValue = useDebounce(searchInputValue, SEARCH_DEBOUNCE_MS);

  // Track when we should show the debouncing state (user finished typing but
  // still within debounce window).
  useEffect(() => {
    if (searchInputValue.trim().length >= MIN_SEARCH_LENGTH && isOpen) {
      setIsDebouncing(true);
    } else {
      setIsDebouncing(false);
    }
  }, [searchInputValue, isOpen]);

  // Actual search call after debounce.
  useEffect(() => {
    if (!isOpen || !trie || !placeMap) return;

    if (debouncedSearchInputValue.trim().length < MIN_SEARCH_LENGTH) {
      setPlaceOptions([]);
      setIsSearching(false);
      setIsDebouncing(false);
      return;
    }

    const runSearch = () => {
      setIsSearching(true);
      setSearchError(null);
      setIsDebouncing(false);

      try {
        const searchTerm = debouncedSearchInputValue.toLowerCase();
        const cleanedSearchTerm = searchTerm.replace(/,/g, '');
        
        // Search the trie (which now contains all variations)
        const searchResults = trie.search(cleanedSearchTerm);

        // Map the search results to Place objects and add formatted display names
        const options = searchResults
          .map((name) => {
            const place = placeMap.get(name);
            if (place) {
              return {
                ...place,
                displayName: formatPlaceName(place.name)
              };
            }
            return undefined;
          })
          .filter((p): p is Place & { displayName: string } => p !== undefined);

        // Deduplicate by place_fips since multiple keys can point to the same place
        const uniqueOptions = options.filter((option, index, self) => 
          index === self.findIndex(o => o.place_fips === option.place_fips)
        );

        console.log(`[Search Debug] "${cleanedSearchTerm}": ${searchResults.length} trie results -> ${uniqueOptions.length} unique places`);
        setPlaceOptions(uniqueOptions);
      } catch (err: any) {
        console.error('Error searching jurisdictions:', err);
        setSearchError('Failed to search for jurisdictions.');
        setPlaceOptions([]);
      } finally {
        setIsSearching(false);
      }
    };

    runSearch();
  }, [debouncedSearchInputValue, isOpen, trie, placeMap]);

  const clearResults = useCallback(() => {
    setPlaceOptions([]);
    setSearchError(null);
  }, []);

  return {
    searchInputValue,
    setSearchInputValue,
    placeOptions,
    isSearching,
    isDebouncing,
    searchError,
    clearResults,
  };
}; 